import { createContext, memo, type ReactNode, useContext, useMemo } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { isDevOrTestStage } from '~/swr-hooks/utils';

export type WorkspaceAgentPanelBodyElementContextValue = {
  element: HTMLElement | null;
};

const defaultContextValue: WorkspaceAgentPanelBodyElementContextValue = {
  element: null,
};

const WorkspaceAgentPanelBodyElementContext =
  createContext<WorkspaceAgentPanelBodyElementContextValue>(defaultContextValue);

export type WorkspaceAgentPanelBodyElementProviderProps = {
  children: ReactNode;
  element: HTMLElement | null;
};

export const WorkspaceAgentPanelBodyElementProvider = memo(
  ({ children, element }: WorkspaceAgentPanelBodyElementProviderProps) => {
    const value = useMemo(() => ({ element }), [element]);

    return (
      <WorkspaceAgentPanelBodyElementContext.Provider value={value}>
        {children}
      </WorkspaceAgentPanelBodyElementContext.Provider>
    );
  },
);

WorkspaceAgentPanelBodyElementProvider.displayName = 'WorkspaceAgentPanelBodyElementProvider';

export const useWorkspaceAgentPanelBodyElement = (): WorkspaceAgentPanelBodyElementContextValue => {
  const value = useContext(WorkspaceAgentPanelBodyElementContext);

  if (value === defaultContextValue) {
    const error = new Error(
      'useWorkspaceAgentPanelBodyElement must be used within WorkspaceAgentPanelBodyElementProvider',
    );

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({
      context: 'Workspace agent panel body element context not found',
      error,
    });
  }

  return value;
};
